/**
 * MobileDriveSectionMenu — the drive's destinations (Home · Library · My Space
 * (orgs) · Projects · MiniApps · Shared) shown in the SAME frosted picker as the workspace
 * switcher (MobileWorkspaceSwitcher): deep fog backdrop (§10.5 transient-
 * popover glass) + a full-screen `safe center`ed sheet of .ws-home-tile
 * cards, the current section carrying the shine border. Opened by tapping the
 * header's drive cutout; replaces the old always-on chip row (MobileDriveNav).
 *
 * Destinations + active logic come from the shared useDriveNavTargets hook, so
 * they stay identical to desktop's DriveShell: Library / My Space / Shared are
 * id-addressed folders that highlight only AT their folder; My Space appears
 * only in an org, Shared only when the tenant has that folder.
 *
 * Portals to document.body (outside any ThemedArea), so it applies the theme
 * class + ws-clear explicitly — same rules as MobileWorkspaceSwitcher.
 */
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useLocation, useNavigate } from 'react-router-dom'
import { House, Clock, SquareLibrary, UserRound, LayoutGrid, Users } from 'lucide-react'
import { useTranslation } from '@papan/i18n'
import { useTheme, getThemeClass, PDAppIcon } from '@papan/core-ui/ui'
import { useBreadcrumbs, useDriveNavTargets } from '@papan/files/client/queries'
import { useTenant } from '../hooks/useTenant'

/** The drive's section destinations + which one is active for this pathname.
 *  Same targets/logic as desktop's DriveShell (shared useDriveNavTargets). */
export function useDriveSections() {
  const { t } = useTranslation('files')
  const location = useLocation()
  const { tenantId, isPersonal, activeOrgEmail } = useTenant()
  const prefix = isPersonal ? '/~' : `/t/${tenantId}`
  // Parse the folder id from the PATHNAME, not useParams — this hook runs in
  // MobileHeader (and the portal menu), both OUTSIDE the <Routes>, so the
  // route's :folderId param isn't in context there.
  const folderId = location.pathname.match(/\/folder\/([^/?]+)/)?.[1]
  const { libraryId, sharedId, mySpaceId } = useDriveNavTargets({ isOrg: !isPersonal, orgEmail: activeOrgEmail })

  // A section is active at its own folder AND anywhere inside it — check the
  // current folder's breadcrumb ancestry, so browsing e.g. Library/29june still
  // highlights Library. (Cached; MobileDriveScreen fetches the same crumbs.)
  const { data: crumbs } = useBreadcrumbs(folderId ?? null)
  const ancestry = new Set((crumbs ?? []).map((c) => c.id))
  const onFolder = (id: string | null) => !!id && (folderId === id || ancestry.has(id))

  return [
    { key: 'home', icon: House, label: t('driveHome.nav.home'), to: `${prefix}/home`, active: /\/home$/.test(location.pathname) },
    { key: 'recent', icon: Clock, label: t('driveHome.nav.recent'), to: `${prefix}/recent`, active: /\/recent$/.test(location.pathname) },
    { key: 'library', icon: SquareLibrary, label: t('driveHome.nav.library'), to: libraryId ? `${prefix}/folder/${libraryId}` : `${prefix}/folder`, active: onFolder(libraryId) },
    ...(mySpaceId ? [{
      key: 'my-space',
      icon: UserRound,
      label: t('driveHome.nav.mySpace'),
      to: `${prefix}/folder/${mySpaceId}`,
      active: onFolder(mySpaceId),
    }] : []),
    { key: 'projects', icon: LayoutGrid, label: t('driveHome.nav.projects'), to: `${prefix}/projects`, active: /\/projects$/.test(location.pathname) },
    { key: 'pdapps', icon: PDAppIcon, label: t('driveHome.nav.miniapps'), to: `${prefix}/pdapps`, active: /\/pdapps$/.test(location.pathname) },
    ...(sharedId ? [{
      key: 'shared',
      icon: Users,
      label: t('driveHome.nav.shared'),
      to: `${prefix}/folder/${sharedId}`,
      active: onFolder(sharedId),
    }] : []),
  ]
}

export function MobileDriveSectionMenu({ ownerLabel, onClose }: { ownerLabel: string; onClose: () => void }) {
  const navigate = useNavigate()
  const { settings } = useTheme()
  const themeClass = getThemeClass(settings.theme.workspace)
  const items = useDriveSections()

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const pick = (to: string) => { navigate(to); onClose() }

  return createPortal(
    // ws-clear is LOAD-BEARING (see MobileWorkspaceSwitcher): without it the
    // theme class paints an opaque --bg-primary and the fog has nothing to frost.
    <div className={`${themeClass} ws-clear fixed inset-0 z-[200]`}>
      <div className="mobile-fog mobile-fog-deep mobile-fog-in absolute inset-0" onClick={onClose} aria-hidden />
      <div
        onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
        className="mobile-sheet-in mobile-account-sheet absolute inset-0 flex w-full flex-col overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        <p className="mobile-switcher-label px-2 text-[10px] uppercase tracking-wide themed-text-muted">{ownerLabel}</p>
        {items.map(({ key, icon: Icon, label, to, active }) => (
          <div
            key={key}
            role="button"
            tabIndex={0}
            onClick={() => pick(to)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') pick(to) }}
            aria-current={active ? 'true' : undefined}
            data-testid={`mobile-drive-nav-${key}`}
            className={`ws-home-tile mobile-account-card relative shrink-0 cursor-pointer ${active ? 'is-selected' : ''}`}
          >
            {active && <span className="mobile-shine-border" aria-hidden />}
            <span className="ws-project-glyph shrink-0">
              <Icon size={16} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm themed-text">{label}</span>
            </span>
          </div>
        ))}
      </div>
    </div>,
    document.body,
  )
}
